/** Server-filtered, cursor-paged audit log. Savings refines loaded rows without another read. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
  limit,
  orderBy,
  query,
  where,
  startAfter
} from 'firebase/firestore';
import { db } from 'config/firebase';
import { useFirebaseAuth } from 'context/FirebaseAuthContext';
import { DECISIONS_COLLECTION } from '../config/rateShopConstants';
import { toDate } from '../lib/describe';
import { useRequestGuard } from './useRequestGuard';

export const DEFAULT_DECISION_FILTERS = Object.freeze({
  search: '',
  status: '',
  storeId: '',
  from: null,
  to: null,
  minSavings: null
});
const PAGE = 200;
const refines = (d, f) => {
  const at = toDate(d.decidedAt);
  return (
    (!f.status || d.status === f.status) &&
    (!f.storeId || d.storeId === f.storeId) &&
    (!f.from || (at && at >= f.from)) &&
    (!f.to || (at && at <= f.to))
  );
};

async function fetchDecisionPage(filters, cursor) {
  const search = String(filters.search || '').trim();
  if (search.startsWith('se-')) {
    const snap = await getDoc(doc(db, DECISIONS_COLLECTION, search));
    const decisions = snap.exists()
      ? [{ id: snap.id, ...snap.data() }].filter(d => refines(d, filters))
      : [];
    return { decisions, cursor: null, hasMore: false };
  }
  const clauses = [];
  if (filters.status) clauses.push(where('status', '==', filters.status));
  if (filters.storeId) clauses.push(where('storeId', '==', filters.storeId));
  if (filters.from) clauses.push(where('decidedAt', '>=', filters.from));
  if (filters.to) clauses.push(where('decidedAt', '<=', filters.to));
  clauses.push(orderBy('decidedAt', 'desc'));
  if (cursor) clauses.push(startAfter(cursor.doc));
  const readPage = field =>
    getDocs(
      query(
        collection(db, DECISIONS_COLLECTION),
        ...(search ? [where(field, '==', search)] : []),
        ...clauses,
        limit(PAGE)
      )
    );
  // Keep the first page's lookup field for later pages, including an empty final page.
  let searchField = cursor?.searchField || 'orderNumber';
  let snap = await readPage(searchField);
  if (search && !cursor && snap.empty) {
    searchField = 'externalOrderId';
    snap = await readPage(searchField);
  }
  const lastDoc = snap.docs[snap.docs.length - 1];
  return {
    decisions: snap.docs.map(d => ({ id: d.id, ...d.data() })),
    cursor: lastDoc ? { doc: lastDoc, searchField } : null,
    hasMore: snap.size === PAGE
  };
}

export function useRateShopDecisions(initialFilters, enabled = true) {
  const [user, authLoading] = useFirebaseAuth();
  const [filters, setFilters] = useState({
    ...DEFAULT_DECISION_FILTERS,
    ...(initialFilters || {})
  });
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const guard = useRequestGuard();
  const { search, status, storeId, from, to } = filters;
  const serverFilters = useMemo(
    () => ({ search, status, storeId, from, to }),
    [search, status, storeId, from, to]
  );
  const refresh = useCallback(async () => {
    if (!guard.mounted()) return;
    const token = guard.begin();
    setLoadingMore(false);
    if (authLoading || !user || !enabled) {
      if (!user) setRows([]);
      setLoading(authLoading);
      setError(null);
      return;
    }
    setLoading(true);
    setHasMore(false);
    setCursor(null);
    setRows([]);
    try {
      const result = await fetchDecisionPage(serverFilters);
      if (!guard.current(token)) return;
      setRows(result.decisions);
      setCursor(result.cursor);
      setHasMore(result.hasMore);
      setError(null);
    } catch (err) {
      if (guard.current(token)) setError(err);
    } finally {
      if (guard.current(token)) setLoading(false);
    }
  }, [authLoading, user, enabled, serverFilters, guard]);
  useEffect(() => {
    refresh();
    return guard.invalidate;
  }, [refresh, guard]);
  const loadMore = useCallback(async () => {
    if (!user || !enabled || loading || loadingMore || !hasMore || !cursor)
      return;
    const token = guard.begin();
    setLoadingMore(true);
    try {
      const result = await fetchDecisionPage(serverFilters, cursor);
      if (!guard.current(token)) return;
      setRows(prev => [
        ...new Map([...prev, ...result.decisions].map(d => [d.id, d])).values()
      ]);
      setCursor(result.cursor);
      setHasMore(result.hasMore);
      setError(null);
    } catch (err) {
      if (guard.current(token)) setError(err);
    } finally {
      if (guard.current(token)) setLoadingMore(false);
    }
  }, [
    user,
    enabled,
    loading,
    loadingMore,
    hasMore,
    cursor,
    serverFilters,
    guard
  ]);
  const decisions = useMemo(
    () =>
      rows.filter(
        d =>
          filters.minSavings == null ||
          (Number(d.savings?.vsCurrent) || 0) >= Number(filters.minSavings)
      ),
    [rows, filters.minSavings]
  );
  const getDecision = useCallback(
    async shipmentId => {
      if (!user) throw new Error('Sign in to view Rate Shop');
      const snap = await getDoc(doc(db, DECISIONS_COLLECTION, shipmentId));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    },
    [user]
  );
  const deleteDecision = useCallback(
    async shipmentId => {
      if (!user) throw new Error('Not signed in');
      await deleteDoc(doc(db, DECISIONS_COLLECTION, shipmentId));
      if (guard.mounted())
        setRows(prev => prev.filter(d => d.id !== shipmentId));
    },
    [user, guard]
  );
  return {
    decisions,
    filters,
    setFilters,
    loading,
    loadingMore,
    error,
    refresh,
    getDecision,
    deleteDecision,
    loadMore,
    hasMore,
    loadedCount: rows.length,
    pageSize: PAGE
  };
}
